import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { 
  LayoutDashboard, 
  Server, 
  Palette, 
  Download, 
  Activity, 
  Settings,
  LogOut,
  User,
  Cpu,
  Zap,
  BarChart3,
  FileText,
  Database,
  Code2,
  Shield,
  ClipboardList
} from 'lucide-react'

interface LayoutProps {
  children: React.ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout, hasPermission, isAdmin } = useAuth()
  
  const handleLogout = () => {
    logout()
    navigate('/login')
  }
  
  // Role-based navigation - filter based on permissions
  const allNavigation = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard, permission: null }, // Everyone
    { name: 'Instances', href: '/instances', icon: Server, permission: 'instances.view' },
    { name: 'Themes', href: '/themes', icon: Palette, permission: 'themes.view' },
    { name: 'Update Cores', href: '/update-cores', icon: Download, permission: 'cores.update' },
    { name: 'Conditional Loading', href: '/conditional-loading', icon: Zap, permission: 'system.config' },
    { name: 'Processes', href: '/processes', icon: Activity, permission: 'system.config' },
    { name: 'Metrics', href: '/metrics', icon: BarChart3, permission: 'system.config' },
    { name: 'Logs', href: '/logs', icon: FileText, permission: 'system.logs' },
    { name: 'Cache', href: '/cache', icon: Database, permission: 'system.cache' },
    { name: 'DSL Builder', href: '/dsl-builder', icon: Code2, permission: 'dsl.view' },
    { name: 'Security', href: '/security', icon: Shield, permission: 'system.security' },
    { name: 'Audit Logs', href: '/audit-logs', icon: ClipboardList, permission: 'system.security' },
    { name: 'Settings', href: '/settings', icon: Settings, permission: null }, // Everyone (shows My Account for non-admins)
  ]
  
  // Filter navigation based on user permissions
  const navigation = allNavigation.filter(item => {
    if (item.permission === null) return true // Always show
    if (isAdmin) return true // Admin sees everything
    return hasPermission(item.permission)
  })
  
  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="w-64 bg-gradient-to-b from-gray-900 to-gray-800 border-r border-gray-700">
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center h-16 px-6 border-b border-gray-700">
            <Cpu className="h-6 w-6 text-blue-400 mr-3" strokeWidth={2} />
            <h1 className="text-xl font-bold text-white">Ikabud</h1>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href
              const Icon = item.icon
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                    isActive
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/50'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  <Icon className="w-5 h-5 mr-3" strokeWidth={isActive ? 2.5 : 2} />
                  {item.name}
                </Link>
              )
            })}
          </nav>
          
          {/* User Menu */}
          <div className="p-4 border-t border-gray-700">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full ring-2 ring-blue-400/30">
                  <User className="w-4 h-4 text-white" strokeWidth={2.5} />
                </div>
                <div className="ml-3">
                  <div className="text-sm font-medium text-white">
                    {user?.username || 'Admin'}
                  </div>
                  <div className="text-xs text-gray-400">
                    {user?.role || 'Administrator'}
                  </div>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                title="Logout"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
            <div className="text-xs text-gray-500">
              <div>Kernel v1.3.0</div>
            </div>
          </div>
        </div>
      </aside>
      
      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <div className="p-8">
          {children}
        </div>
      </main>
    </div>
  )
}
